import * as React from "react";
import { Field, FieldProps, Input, InputProps, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

const useStyles = makeStyles({
    inputBase: {
        "&&&": {
            "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        },
        // Fix: mobile webviews don't render the blinking caret inside inputs
        // within a fixed-position overlay. A compositing layer forces proper rendering.
        "& input": {
            transform: "translateZ(0)",
            "caret-color": "auto",
        },
        // When not focused, truncate overflowing text with '...' instead of letting it hide silently;
        // while focused, let the native input scroll to the caret as usual.
        "& input:not(:focus)": {
            overflow: "hidden",
            "text-overflow": "ellipsis",
            "white-space": "nowrap",
        },
    },
    inputHover: {
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
});

export interface NewformaInputProps
    extends Pick<
        InputProps,
        "value" | "onChange" | "disabled" | "maxLength" | "autoComplete" | "placeholder" | "className" | "style"
    > {
    label?: FieldProps["label"];
    required?: FieldProps["required"];
    validationMessage?: FieldProps["validationMessage"];
    validationState?: FieldProps["validationState"];
    id?: string;
    "data-testid"?: string;
    fieldDataTestId?: string;
}

export const NewformaInput = (props: NewformaInputProps): JSX.Element => {
    const styles = useStyles();
    const isDarkMode = document.querySelector("[data-theme]")?.getAttribute("data-theme") !== "default";

    return (
        <Field
            label={props.label}
            required={props.required}
            validationMessage={props.validationMessage}
            validationState={props.validationState}
            data-testid={props.fieldDataTestId}
        >
            <Input
                appearance={isDarkMode ? "filled-darker" : "outline"}
                className={mergeClasses(styles.inputBase, !props.disabled && styles.inputHover, props.className)}
                data-testid={props["data-testid"]}
                value={props.value}
                onChange={props.onChange}
                disabled={props.disabled}
                maxLength={props.maxLength}
                autoComplete={props.autoComplete}
                placeholder={props.placeholder}
                style={props.style}
            />
        </Field>
    );
};
